import { useEffect, useMemo, useState } from "react";
import { Flame } from "lucide-react";

/**
 * Streak tracker — localStorage-only, no backend.
 *
 * Props:
 *   storageKey   — unique key per game (e.g. "titans_gtt_streak_v1")
 *   triggerKey   — value that, when it changes, signifies the user completed
 *                  the daily challenge for the first time today. Pass the
 *                  game-completion date (YYYY-MM-DD) once the user submits.
 *                  Pass `null` while idle.
 */
export default function StreakBadge({ storageKey, triggerKey }) {
  const lastKey = `${storageKey}_last`;
  const bestKey = `${storageKey}_best`;
  // Game-state only (streak count, last-played date). NOT auth — safe to store
  // in localStorage; XSS risk is irrelevant for a daily-streak counter.
  const [streak, setStreak] = useState(() => parseInt(localStorage.getItem(storageKey) || "0", 10));
  const [best, setBest] = useState(() => parseInt(localStorage.getItem(bestKey) || "0", 10));

  useEffect(() => {
    if (!triggerKey) return;
    const last = localStorage.getItem(lastKey);
    if (last === triggerKey) return; // already counted today

    // Yesterday in UTC ISO
    const t = new Date(`${triggerKey}T00:00:00Z`);
    t.setUTCDate(t.getUTCDate() - 1);
    const yesterday = t.toISOString().slice(0, 10);

    let next;
    if (!last) {
      next = 1;
    } else if (last === yesterday) {
      next = streak + 1;
    } else {
      next = 1; // skipped a day → restart
    }
    localStorage.setItem(storageKey, String(next));
    localStorage.setItem(lastKey, triggerKey);
    const newBest = Math.max(best, next);
    localStorage.setItem(bestKey, String(newBest));
    setStreak(next);
    setBest(newBest);
  }, [triggerKey, storageKey, lastKey, bestKey, streak, best]);

  const tone = useMemo(() => {
    if (streak >= 30) return { color: "text-purple-300", glow: "border-purple-500" };
    if (streak >= 14) return { color: "text-titans-red", glow: "border-titans-red" };
    if (streak >= 7)  return { color: "text-orange-400", glow: "border-orange-500" };
    if (streak >= 3)  return { color: "text-titans-blue", glow: "border-titans-blue" };
    return { color: "text-titans-silver", glow: "border-titans-silver/30" };
  }, [streak]);

  if (streak < 1 && best < 1) return null;

  return (
    <div
      data-testid={`streak-${storageKey}`}
      className={`inline-flex items-center gap-2 border px-3 py-1.5 ${tone.glow}`}
      title={`Current: ${streak} · Best: ${best}`}
    >
      <Flame size={14} className={tone.color} />
      <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver leading-none">
        Streak
      </div>
      <div className={`font-display text-base leading-none ${tone.color}`}>{streak}</div>
      {best > streak && (
        <div className="font-cond uppercase tracking-wider text-[9px] text-titans-muted leading-none ml-1">
          (Best {best})
        </div>
      )}
    </div>
  );
}
